import React, {
  Suspense,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
  useContext,
} from 'react';
import Cookies from 'js-cookie';
import { Box, Portal, CircularProgress, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { IntlProvider } from 'react-intl';
import {
  GlobalCopilotProps,
  CopilotAppApis,
  CopilotAction,
  FooterPlusDropdownRenderProp,
} from './types';
import useVisitorEmail from './hooks/useVisitorEmail';
import { useCopilotAction } from './CopilotActionContext';
import { useCopilotMethods } from './CopilotMethodsContext';
import AnonymousIdentityGuard from '../Room/View/AnonymousIdentityGuard';
import BuyerCopilotButton from '../BuyerCopilot/BuyerCopilotButton';
import ActionsMenu from './components/ActionsMenu';
import { ContactSchema } from '../../definitions/schemas';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';
import CopilotErrorBoundary from './components/ErrorBoundary';
import CopilotApp from './FederatedCopilotApp';
import {
  COPILOT_DOMAINS,
  COPILOT_FEATURES,
  COPILOT_ACTION_TYPES,
  PAYLOAD_TYPES,
  CUSTOM_TYPE_NAME,
  REGION_HEADER_NAME,
} from './constants';
import ContactNameEditor from '../Contact/ContactNameEditor';
import { Referrers } from '../../enums/Analytics';
import UserMessage from './components/UserMessageNode';
import PostToGroupChatMessage from './components/PostToGroupChatMessage';
import { Mention as MentionPillType } from '../Room/components/BuyerCopilotChats/MentionsNode/MentionPill';
import {
  MENTION_TOKEN_CAPTURE_REGEX,
  MENTION_TOKEN_SPLIT_REGEX,
} from '../Room/components/BuyerCopilotChats/MentionsNode/constants';
import { trimAnswerNotFoundPostPrompt } from '../Room/components/BuyerCopilotChats/helper';
import { FooterMessage } from './components/FooterMessage';
import { DRAWER_Z_INDEX } from '../../definitions/constants';
import { FullScreenTrackingContext } from '../Rooms/Tracking/FullScreenTrackingContext';
import { useCopilotTrackEvents } from './hooks/useCopilotTrackEvents';
import { OrganizationThemeContext } from '../../lib/context/OrganizationThemeContext';
import BuyerCopilotAvatarIcon from '../BuyerCopilot/BuyerCopilotAvatarIcon';
import { FooterPlusDropdown } from './components/FooterPlusDropdown';
import { useI18n } from '../../lib/hook/useI18n';
import RequestConfigurationFactory from '../../Services/Request/RequestConfigurationFactory';
import { MULTI_REGION_COOKIE_NAME } from '../../config/env';
import { getPublicXToken } from '../../lib/helper/AuthHelper';

const useStyles = makeStyles((theme: Theme) => ({
  floatingContainer: {
    position: 'fixed',
    bottom: theme.spacing(2.5),
    right: theme.spacing(2.5),
    zIndex: 2147483647,
  },
  chatboxContainer: (props: {
    isExpanded: boolean;
    buttonBackgroundColor?: string;
  }) => ({
    position: 'fixed',
    width: props.isExpanded ? '590px' : '500px',
    height: `calc(100vh - ${props.isExpanded ? '100px' : '200px'})`,
    bottom: theme.spacing(10),
    right: theme.spacing(2.5),
    zIndex: 2147483647,
    transition: theme.transitions.create(['width', 'height'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100vw',
      left: 0,
      right: 0,
      top: 0,
      bottom: theme.spacing(10),
      height: 'auto',
    },
    '& .send-message-button': {
      color: 'var(--text-primary-light) !important',
      top: `${theme.spacing(1.75)} !important`,
      backgroundColor: `${
        props.buttonBackgroundColor || 'var(--buyer-copilot-enabled)'
      } !important`,
      '&.disabled': {
        color: 'var(--color-text-disabled) !important',
        backgroundColor: 'var(--color-bg-disabled) !important',
      },
    },
    '& :has(> .ql-container:focus-within)': {
      borderColor: `${
        props.buttonBackgroundColor || 'var(--buyer-copilot-enabled)'
      } !important`,
    },
    '& .ql-mention-list-container': {
      maxWidth: '400px',
    },
    '& .chatbox-buyer-copilot-drawer-body': {
      border: '1px solid var(--color-bg-disabled)',
    },
    '& .ql-blank::before': {
      marginLeft: theme.spacing(3.5),
      lineHeight: theme.spacing(3),
    },
    '& .chat-input .ql-editor': {
      paddingTop: `${theme.spacing(2.25)} !important`,
      paddingLeft: `${theme.spacing(4.5)} !important`,
      '& p': {
        padding: `0px !important`,
      },
    },
    '& .custom-header': {
      paddingLeft: `12px !important`,
      borderBottom: '1px solid var(--color-bg-disabled)',
    },
    '& .footer-actions': {
      lineHeight: `${theme.spacing(2.25)} !important`,
      top: `${theme.spacing(2.5)} !important`,
      left: `${theme.spacing(1.625)} !important`,
    },
  }),
  userMessage: {
    backgroundColor: 'var(--text-primary-light)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
  },
}));

const Loader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
    <CircularProgress color="secondary" size={40} />
  </Box>
);

const UserNode =
  (
    mentions: MentionPillType[],
    handleAction: (mention: MentionPillType) => void
  ) =>
  (message: any) =>
    (
      <UserMessage
        message={message.text}
        mentions={mentions}
        onMentionClick={handleAction}
      />
    );

const GlobalCopilot: React.FC<GlobalCopilotProps> = ({
  isConversationEnabled,
  cname,
  tenantId,
  roomId,
  roomUuid,
  chatContext: propChatContext,
  isVisible,
  openVisibilityWithDefaultMessage,
  closeVisibility,
  toggleVisibility,
  onAction,
  author,
  onContactEdited,
  defaultInputMessage,
  mentions,
  mentionsLoading,
  mentionsError,
  onPostToGroupChat,
}) => {
  const { t } = useI18n();
  const email = useVisitorEmail(roomUuid);
  const [copilotContainer, setCopilotContainer] =
    useState<HTMLDivElement | null>(null);
  const isCopilotInteractionDisabled = !author?.id;
  const copilotButtonTooltip = isCopilotInteractionDisabled
    ? t('room.buyer_copilot.live_room_only_tooltip')
    : '';
  const [isContactEditorVisible, setIsContactEditorVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const chatRef = useRef<CopilotAppApis | null>(null);
  const { buttonsStyles } = useContext(OrganizationThemeContext);
  const classes = useStyles({
    isExpanded,
    buttonBackgroundColor: buttonsStyles?.backgroundColor,
  });
  const copilotMethods = useCopilotMethods();

  const copilotAction = useCopilotAction();

  // The launcher and chatbox float above every overlay (max z-index), so
  // hide them while a full-screen surface (MAP editor, signable signing,
  // full-screen content) covers the room
  const { fullScreenContentId } = useContext(FullScreenTrackingContext);
  const isRoomCovered = !!fullScreenContentId;

  const currentOrg = OrganizationsStore.getCurrentOrganization();

  const authContext = useMemo(() => {
    const userId = author?.id?.toString() || '';

    return {
      'company-id': cname,
      'org-id': tenantId,
      'contact-id': userId,
      'location-id': roomUuid,
      'x-token': getPublicXToken(cname, tenantId, userId, roomUuid),
    };
  }, [cname, tenantId, author?.id, roomUuid]);

  const wsQueryParams = useMemo(() => {
    if (!MULTI_REGION_COOKIE_NAME) {
      return propChatContext?.wsQueryParams;
    }
    const requestHeaders = RequestConfigurationFactory.buildRequestHeaders({});
    const regionFromHeader = (
      requestHeaders as Record<string, string | undefined>
    )[MULTI_REGION_COOKIE_NAME];
    const regionFromCookie = Cookies.get(MULTI_REGION_COOKIE_NAME);
    const region = regionFromHeader || regionFromCookie;

    if (!region) {
      return propChatContext?.wsQueryParams;
    }

    return {
      ...propChatContext?.wsQueryParams,
      [REGION_HEADER_NAME]: region,
    };
  }, [propChatContext?.wsQueryParams]);

  const chatContext = useMemo(
    () => ({
      ...propChatContext,
      authContext,
      user: {
        email,
        isAuthenticated: false,
      },
      domain: COPILOT_DOMAINS.DSR,
      featureName: COPILOT_FEATURES.BUYER_COPILOT,
      contactId: author?.id?.toString(),
      dsrOrgId: currentOrg?.id?.toString(),
      locationId: roomUuid,
      sessionMode: 'PERSISTENT',
      sessionModesToResume: ['PERSISTENT'],
      wsQueryParams,
      roomUuid,
      parentContext: {
        roomUuid,
      },
    }),
    [
      propChatContext,
      authContext,
      email,
      author?.id,
      currentOrg?.id,
      roomUuid,
      wsQueryParams,
    ]
  );

  const handleAction = useCallback(
    (mention: MentionPillType) => {
      const action: CopilotAction = {
        type: COPILOT_ACTION_TYPES.OPEN_CONTENT,
        payload: {
          id: mention.id,
        },
      };
      copilotAction?.onCopilotAction(action);
      onAction?.(action);
    },
    [copilotAction, onAction]
  );

  const handleTrackEvents = useCopilotTrackEvents(mentions);

  const handleGetRef = useCallback(
    (_ref: CopilotAppApis) => {
      chatRef.current = _ref;
      copilotMethods?.registerApis(_ref);
    },
    [copilotMethods]
  );

  const closeContactNameEditor = useCallback(() => {
    setIsContactEditorVisible(false);
  }, []);

  const handleContactNameEdited = useCallback(
    (newContact: ContactSchema) => {
      setIsContactEditorVisible(false);
      onContactEdited?.(newContact);
    },
    [onContactEdited]
  );

  const handleBuyerCopilotButtonClick = useCallback(() => {
    if (!author?.id) {
      return;
    }
    if (isVisible) {
      closeVisibility();
    } else {
      openVisibilityWithDefaultMessage('');
    }
  }, [
    isVisible,
    closeVisibility,
    openVisibilityWithDefaultMessage,
    author?.id,
  ]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const chatboxContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isVisible) return;

    const stopFocusTrapHijack = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (chatboxContainerRef.current?.contains(target)) {
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener('focusin', stopFocusTrapHijack, true);
    return () =>
      document.removeEventListener('focusin', stopFocusTrapHijack, true);
  }, [isVisible]);

  const userMessageNode = useMemo(
    () => UserNode(mentions || [], handleAction),
    [mentions, handleAction]
  );

  const mentionInputProps = useMemo(
    () => ({
      maxMentionLength: 20,
    }),
    []
  );

  const handlePostToGroupChat = useCallback(
    (message: string) => {
      closeVisibility();
      onPostToGroupChat?.(message);
    },
    [closeVisibility, onPostToGroupChat]
  );

  const afterReceive = useCallback(
    (message: any, isHistory: boolean) => {
      if (
        message?.type === PAYLOAD_TYPES.CUSTOM_COMPONENT &&
        message?.customTypeName ===
          CUSTOM_TYPE_NAME.BUYER_COPILOT_ANSWER_NOT_FOUND
      ) {
        let payload: { answer?: string; userMessage?: string } = {};
        try {
          payload = JSON.parse(message.metadataJson || '{}');
        } catch {
          payload = {};
        }
        if (payload.userMessage && mentions?.length) {
          payload.userMessage = payload.userMessage
            .split(MENTION_TOKEN_SPLIT_REGEX)
            .map((part) => {
              const match = part.match(MENTION_TOKEN_CAPTURE_REGEX);
              if (match) {
                const mention = mentions.find((m) => m.uuid === match[1]);
                return mention ? `@${mention.value}` : part;
              }
              return part;
            })
            .join('');
        }
        const props = {
          message: isConversationEnabled
            ? payload.answer
            : trimAnswerNotFoundPostPrompt(payload.answer || '', {
                type: CUSTOM_TYPE_NAME.BUYER_COPILOT_ANSWER_NOT_FOUND,
              }),
          onPostToGroupChat: () =>
            handlePostToGroupChat(payload.userMessage || payload.answer || ''),
        };
        const CustomRenderer = () => (
          <PostToGroupChatMessage
            {...props}
            isConversationEnabled={isConversationEnabled}
          />
        );
        if (isHistory) {
          return { component: CustomRenderer };
        }
        chatRef?.current?.addCustomComponent(CustomRenderer, message);
        return { ...message, customTypeName: 'copilot-empty-message' };
      }
      return message;
    },
    [handlePostToGroupChat, mentions, isConversationEnabled]
  );

  return (
    <>
      {isVisible && copilotContainer && (
        <Portal>
          <div
            ref={chatboxContainerRef}
            className={classes.chatboxContainer}
            style={{ display: isRoomCovered ? 'none' : undefined }}
          >
            <IntlProvider messages={{}} defaultLocale="en">
              <CopilotErrorBoundary>
                <Suspense fallback={<Loader />}>
                  <CopilotApp
                    title="Copilot"
                    scrollBarMaxHeight="auto"
                    open={isVisible}
                    onOpenChange={toggleVisibility}
                    inputCharLimit={1000}
                    chatContext={chatContext}
                    onMentionClick={handleAction}
                    className="chatbox-buyer-copilot-drawer-body"
                    trackEvents={handleTrackEvents}
                    getRef={handleGetRef}
                    withoutAuth
                    defaultInputValue={defaultInputMessage}
                    userMessageNode={userMessageNode}
                    container={copilotContainer}
                    skin="inLayout"
                    mentions={mentions}
                    afterReceive={afterReceive}
                    enableCopilotChatHistory
                    customFooterContent={<FooterMessage />}
                    hideFooterNoteHelp
                    inputPlaceholder="Ask about anything shared in this room..."
                    avatarIcon={<BuyerCopilotAvatarIcon />}
                    hideDeleteChatIcon
                    hideCloseIcon
                    mentionInputProps={mentionInputProps}
                    footerActions={
                      ((opts: Parameters<FooterPlusDropdownRenderProp>[0]) => (
                        <FooterPlusDropdown
                          insertAtContext={() =>
                            chatRef.current?.textInputRef?.current?.openMentionMenu?.(
                              '@'
                            )
                          }
                          disabled={
                            opts.disabled || mentionsLoading || mentionsError
                          }
                        />
                      )) as FooterPlusDropdownRenderProp
                    }
                  />
                </Suspense>
              </CopilotErrorBoundary>
            </IntlProvider>
          </div>
        </Portal>
      )}

      {isContactEditorVisible && (
        <ContactNameEditor
          contact={author!}
          modalZIndex={DRAWER_Z_INDEX + 1}
          onClose={closeContactNameEditor}
          onSave={handleContactNameEdited}
          open={isContactEditorVisible}
          referrer={Referrers.DEAL_ROOMS.BUYER_COPILOT}
          roomId={roomId}
        />
      )}

      <Portal>
        <div
          ref={(node) => {
            if (node && !copilotContainer) {
              setCopilotContainer(node);
            }
          }}
          className={classes.floatingContainer}
          style={{ display: isRoomCovered ? 'none' : undefined }}
        >
          <AnonymousIdentityGuard referrer="Deal Room - Chatbot">
            <Box display="flex" flexDirection="row" gap={2}>
              {isVisible && (
                <ActionsMenu
                  onDeleteChatHistory={copilotMethods?.deleteChatHistory!}
                  isExpanded={isExpanded}
                  onExpandToggle={handleExpandToggle}
                />
              )}
              <BuyerCopilotButton
                onClick={handleBuyerCopilotButtonClick}
                showCloseIcon={isVisible}
                tooltipTitle={copilotButtonTooltip}
              />
            </Box>
          </AnonymousIdentityGuard>
        </div>
      </Portal>
    </>
  );
};

export default GlobalCopilot;
